(() => {
  if (window.__bubblesAngelsHomepageScriptLoaded) return;
  window.__bubblesAngelsHomepageScriptLoaded = true;

  const cleanupRegistry = new WeakMap();

  const bind = (target, eventName, handler, options) => {
    target.addEventListener(eventName, handler, options);
    return () => target.removeEventListener(eventName, handler, options);
  };

  const registerCleanup = (element, cleanup) => {
    cleanupRegistry.set(element, cleanup);
  };

  const destroy = (root = document) => {
    root.querySelectorAll('[data-ba-bound="true"]').forEach((element) => {
      const cleanup = cleanupRegistry.get(element);
      if (typeof cleanup === 'function') cleanup();
      cleanupRegistry.delete(element);
      delete element.dataset.baBound;
    });
  };

  const setupHeroSlider = (hero) => {
    if (hero.dataset.baBound === 'true') return;

    const viewport = hero.querySelector('.hero__viewport');
    const track = hero.querySelector('[data-hero-track]');
    const slides = Array.from(hero.querySelectorAll('[data-hero-slide]'));

    if (!viewport || !track || slides.length === 0) return;

    hero.dataset.baBound = 'true';

    const total = slides.length;
    const autoplayMs = Math.max(Number(hero.dataset.autoplayMs || 6500), 2500);
    const cleanups = [];
    let currentIndex = 0;
    let startX = 0;
    let startY = 0;
    let deltaX = 0;
    let pointerIsDown = false;
    let isDragging = false;
    let capturedPointerId = null;
    let autoplayId = null;

    if (total < 2) {
      hero.classList.add('is-static');
      slides.forEach((slide) => {
        slide.setAttribute('aria-hidden', 'false');
      });
      registerCleanup(hero, () => {
        hero.classList.remove('is-static');
      });
      return;
    }

    const sync = () => {
      slides.forEach((slide, index) => {
        slide.setAttribute('data-active', index === currentIndex ? 'true' : 'false');
        slide.setAttribute('aria-hidden', index === currentIndex ? 'false' : 'true');
      });
    };

    const stopAutoplay = () => {
      if (!autoplayId) return;
      window.clearInterval(autoplayId);
      autoplayId = null;
    };

    const startAutoplay = () => {
      stopAutoplay();
      autoplayId = window.setInterval(() => {
        currentIndex = (currentIndex + 1) % total;
        sync();
      }, autoplayMs);
    };

    const endDrag = () => {
      if (!pointerIsDown) return;

      if (isDragging) {
        const threshold = Math.min(viewport.offsetWidth * 0.18, 72);

        if (deltaX > threshold) {
          currentIndex = currentIndex === 0 ? total - 1 : currentIndex - 1;
        } else if (deltaX < -threshold) {
          currentIndex = (currentIndex + 1) % total;
        }
      }

      pointerIsDown = false;
      isDragging = false;
      capturedPointerId = null;
      deltaX = 0;
      viewport.classList.remove('is-dragging');
      track.classList.remove('is-dragging');
      sync();
      startAutoplay();
    };

    const onPointerDown = (event) => {
      startX = event.clientX;
      startY = event.clientY;
      deltaX = 0;
      pointerIsDown = true;
      capturedPointerId = event.pointerId;
      stopAutoplay();
      // Do NOT capture or set isDragging yet — wait for confirmed horizontal drag intent
    };

    const onPointerMove = (event) => {
      if (!pointerIsDown) return;
      const dx = event.clientX - startX;
      const dy = event.clientY - startY;

      if (!isDragging) {
        const absDx = Math.abs(dx);
        const absDy = Math.abs(dy);
        if (absDx > 8 && absDx > absDy) {
          isDragging = true;
          viewport.classList.add('is-dragging');
          track.classList.add('is-dragging');
          viewport.setPointerCapture(capturedPointerId);
        } else if (absDy > 8) {
          pointerIsDown = false;
          capturedPointerId = null;
          startAutoplay();
        }
        return;
      }

      deltaX = dx;
    };

    const onVisibilityChange = () => {
      if (document.hidden) {
        stopAutoplay();
        return;
      }

      if (!isDragging) startAutoplay();
    };

    cleanups.push(bind(viewport, 'pointerdown', onPointerDown));
    cleanups.push(bind(viewport, 'pointermove', onPointerMove));
    cleanups.push(bind(viewport, 'pointerup', endDrag));
    cleanups.push(bind(viewport, 'pointercancel', endDrag));
    cleanups.push(bind(document, 'visibilitychange', onVisibilityChange));

    sync();
    startAutoplay();

    registerCleanup(hero, () => {
      stopAutoplay();
      viewport.classList.remove('is-dragging');
      track.classList.remove('is-dragging');
      cleanups.forEach((cleanup) => cleanup());
    });
  };

  const setupHairQuiz = (quiz) => {
    if (quiz.dataset.baBound === 'true') return;

    const viewport = quiz.querySelector('.hair-quiz__viewport');
    const track = quiz.querySelector('[data-quiz-track]');
    const cards = Array.from(quiz.querySelectorAll('[data-quiz-step]'));
    const progress = quiz.querySelector('[data-quiz-progress]');

    if (!viewport || !track || !progress || cards.length === 0) return;

    quiz.dataset.baBound = 'true';

    const cleanups = [];
    const total = cards.length;
    let currentStep = 0;
    let dragStartX = 0;
    let dragStartY = 0;
    let dragDeltaX = 0;
    let pointerIsDown = false;
    let isDragging = false;
    let capturedPointerId = null;
    const answers = new Map();

    quiz.style.setProperty('--quiz-total-steps', String(total));

    const getMaxUnlockedStep = () => {
      if (quiz.classList.contains('is-complete')) return total - 1;
      return Math.min(Math.max(answers.size, currentStep), total - 1);
    };

    const syncProgress = () => {
      const progressStep = quiz.classList.contains('is-complete') ? total : currentStep + 1;
      progress.style.width = `${(progressStep / total) * 100}%`;
    };

    const syncTrack = (offset = 0) => {
      cards.forEach((card, index) => {
        card.toggleAttribute('data-active', index === currentStep);
      });

      const activeCard = cards[currentStep];
      if (!activeCard) return;

      track.style.transform = `translateX(${-(activeCard.offsetLeft) + offset}px)`;
    };

    const syncButtons = () => {
      cards.forEach((card, index) => {
        const next = card.querySelector('[data-quiz-next]');
        if (!next) return;
        next.disabled = index !== currentStep || !answers.has(index);
      });
    };

    cards.forEach((card, index) => {
      const options = Array.from(card.querySelectorAll('[data-quiz-option]'));
      const next = card.querySelector('[data-quiz-next]');

      options.forEach((option) => {
        const onOptionClick = () => {
          if (index !== currentStep) return;

          options.forEach((item) => {
            item.classList.remove('is-selected');
            item.setAttribute('aria-pressed', 'false');
          });

          option.classList.add('is-selected');
          option.setAttribute('aria-pressed', 'true');
          answers.set(index, option.dataset.value);
          syncButtons();
        };

        cleanups.push(bind(option, 'click', onOptionClick));
      });

      if (!next) return;

      const onNextClick = () => {
        if (next.disabled) return;

        if (currentStep < total - 1) {
          currentStep += 1;
          dragDeltaX = 0;
          syncTrack();
          syncButtons();
          syncProgress();
          return;
        }

        const nextUrl = next.dataset.quizNextUrl;
        if (nextUrl) {
          window.location.href = nextUrl;
          return;
        }

        quiz.classList.add('is-complete');
        syncProgress();
      };

      cleanups.push(bind(next, 'click', onNextClick));
    });

    const endDrag = () => {
      if (!pointerIsDown) return;

      if (isDragging) {
        const threshold = Math.min(viewport.offsetWidth * 0.18, 72);
        const maxUnlockedStep = getMaxUnlockedStep();

        if (dragDeltaX > threshold && currentStep > 0) {
          currentStep -= 1;
        } else if (dragDeltaX < -threshold && currentStep < maxUnlockedStep) {
          currentStep += 1;
        }
      }

      pointerIsDown = false;
      isDragging = false;
      capturedPointerId = null;
      dragDeltaX = 0;
      viewport.classList.remove('is-dragging');
      track.classList.remove('is-dragging');
      syncTrack();
      syncButtons();
      syncProgress();
    };

    const onPointerDown = (event) => {
      dragStartX = event.clientX;
      dragStartY = event.clientY;
      dragDeltaX = 0;
      pointerIsDown = true;
      capturedPointerId = event.pointerId;
      // Do NOT capture or set isDragging yet — wait for confirmed horizontal drag intent
    };

    const onPointerMove = (event) => {
      if (!pointerIsDown) return;
      const dx = event.clientX - dragStartX;
      const dy = event.clientY - dragStartY;

      if (!isDragging) {
        const absDx = Math.abs(dx);
        const absDy = Math.abs(dy);
        if (absDx > 8 && absDx > absDy) {
          // Confirmed horizontal drag — now capture and enter drag mode
          isDragging = true;
          viewport.classList.add('is-dragging');
          track.classList.add('is-dragging');
          viewport.setPointerCapture(capturedPointerId);
        } else if (absDy > 8) {
          // Vertical scroll intent — cancel tracking so native scroll works
          pointerIsDown = false;
          capturedPointerId = null;
        }
        return;
      }

      dragDeltaX = dx;
      syncTrack(dragDeltaX);
    };

    const onPointerLeave = () => {
      if (isDragging) {
        endDrag();
      } else {
        pointerIsDown = false;
        capturedPointerId = null;
      }
    };

    cleanups.push(bind(window, 'resize', syncTrack));
    cleanups.push(bind(viewport, 'pointerdown', onPointerDown));
    cleanups.push(bind(viewport, 'pointermove', onPointerMove));
    cleanups.push(bind(viewport, 'pointerup', endDrag));
    cleanups.push(bind(viewport, 'pointercancel', endDrag));
    cleanups.push(bind(viewport, 'pointerleave', onPointerLeave));

    syncTrack();
    syncButtons();
    syncProgress();

    registerCleanup(quiz, () => {
      viewport.classList.remove('is-dragging');
      track.classList.remove('is-dragging');
      cleanups.forEach((cleanup) => cleanup());
    });
  };

  const setupCompareSlider = (slider) => {
    if (slider.dataset.baBound === 'true') return;

    const beforeLayer = slider.querySelector('[data-compare-before]');
    const divider = slider.querySelector('[data-compare-divider]');
    const range = slider.querySelector('[data-compare-range]');

    if (!beforeLayer || !divider || !range) return;

    slider.dataset.baBound = 'true';

    const cleanups = [];
    let isDragging = false;

    const applyPosition = (value) => {
      const clamped = Math.max(0, Math.min(100, Number(value)));
      slider.style.setProperty('--compare-position', `${clamped}%`);
      beforeLayer.style.clipPath = `inset(0 ${100 - clamped}% 0 0)`;
      divider.style.left = `${clamped}%`;
      range.value = String(clamped);
    };

    const setFromPointer = (clientX) => {
      const rect = slider.getBoundingClientRect();
      const ratio = ((clientX - rect.left) / rect.width) * 100;
      applyPosition(ratio);
    };

    const onRangeInput = () => {
      applyPosition(range.value);
    };

    const onPointerDown = (event) => {
      isDragging = true;
      setFromPointer(event.clientX);
      slider.setPointerCapture(event.pointerId);
      event.preventDefault();
    };

    const onPointerMove = (event) => {
      if (!isDragging) return;
      setFromPointer(event.clientX);
      event.preventDefault();
    };

    const stopDragging = (event) => {
      if (!isDragging) return;
      isDragging = false;
      if (slider.hasPointerCapture(event.pointerId)) {
        slider.releasePointerCapture(event.pointerId);
      }
    };

    const onLostPointerCapture = () => {
      isDragging = false;
    };

    cleanups.push(bind(range, 'input', onRangeInput));
    cleanups.push(bind(slider, 'pointerdown', onPointerDown));
    cleanups.push(bind(slider, 'pointermove', onPointerMove));
    cleanups.push(bind(slider, 'pointerup', stopDragging));
    cleanups.push(bind(slider, 'pointercancel', stopDragging));
    cleanups.push(bind(slider, 'lostpointercapture', onLostPointerCapture));

    applyPosition(slider.dataset.startPosition || 50);

    registerCleanup(slider, () => {
      cleanups.forEach((cleanup) => cleanup());
    });
  };

  const setupTestimonialSlider = (section) => {
    if (section.dataset.baBound === 'true') return;

    const viewport = section.querySelector('.testimonial-featured__viewport');
    const track = section.querySelector('[data-testimonial-track]');
    const slides = Array.from(section.querySelectorAll('[data-testimonial-slide]'));
    const progress = section.querySelector('[data-testimonial-progress]');

    if (!viewport || !track || !progress || slides.length === 0) return;

    section.dataset.baBound = 'true';

    const cleanups = [];
    const desktopQuery = window.matchMedia('(min-width: 768px)');
    let currentIndex = 0;
    let startX = 0;
    let deltaX = 0;
    let isDragging = false;

    const sync = (offset = 0) => {
      if (desktopQuery.matches) {
        track.style.transform = '';
        progress.style.width = '';
        progress.style.transform = 'translateX(0)';
        return;
      }

      const active = slides[currentIndex];
      if (!active) return;

      track.style.transform = `translateX(${-(active.offsetLeft) + offset}px)`;
      const slotWidth = 100 / slides.length;
      progress.style.width = `${slotWidth}%`;
      progress.style.transform = `translateX(${currentIndex * 100}%)`;
    };

    const endDrag = () => {
      if (!isDragging) return;

      const threshold = Math.min(viewport.offsetWidth * 0.18, 72);

      if (deltaX > threshold && currentIndex > 0) {
        currentIndex -= 1;
      } else if (deltaX < -threshold && currentIndex < slides.length - 1) {
        currentIndex += 1;
      }

      isDragging = false;
      deltaX = 0;
      viewport.classList.remove('is-dragging');
      track.classList.remove('is-dragging');
      sync();
    };

    const onPointerDown = (event) => {
      if (desktopQuery.matches) return;
      startX = event.clientX;
      deltaX = 0;
      isDragging = true;
      viewport.classList.add('is-dragging');
      track.classList.add('is-dragging');
      viewport.setPointerCapture(event.pointerId);
    };

    const onPointerMove = (event) => {
      if (!isDragging || desktopQuery.matches) return;
      deltaX = event.clientX - startX;
      sync(deltaX);
    };

    const onMediaChange = () => {
      sync();
    };

    cleanups.push(bind(viewport, 'pointerdown', onPointerDown));
    cleanups.push(bind(viewport, 'pointermove', onPointerMove));
    cleanups.push(bind(viewport, 'pointerup', endDrag));
    cleanups.push(bind(viewport, 'pointercancel', endDrag));
    cleanups.push(bind(desktopQuery, 'change', onMediaChange));
    cleanups.push(bind(window, 'resize', sync));

    sync();

    registerCleanup(section, () => {
      viewport.classList.remove('is-dragging');
      track.classList.remove('is-dragging');
      cleanups.forEach((cleanup) => cleanup());
    });
  };

  const init = (root = document) => {
    root.querySelectorAll('[data-hero-slider]').forEach(setupHeroSlider);
    root.querySelectorAll('[data-hair-quiz]').forEach(setupHairQuiz);
    root.querySelectorAll('[data-compare-slider]').forEach(setupCompareSlider);
    root.querySelectorAll('[data-testimonial-slider]').forEach(setupTestimonialSlider);
  };

  const onSectionLoad = (event) => {
    init(event.target);
  };

  const onSectionUnload = (event) => {
    destroy(event.target);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init(document), { once: true });
  } else {
    init(document);
  }

  document.addEventListener('shopify:section:load', onSectionLoad);
  document.addEventListener('shopify:section:unload', onSectionUnload);
})();
